import { LonaWebComponent, html } from "@tento-ui/component";
import { Div } from "@tento-ui/component-builtin";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { EventItemPresentation } from "@tento-lona";
import { EventItem, type Option } from "@tento-lona/sheets";

/**
 * The "more" badge on a day whose all-day events do not fit, with a tooltip
 * listing them.
 */
@component({
  name: "lona-all-day-events-more-indicator",
})
export class MoreIndicator extends LonaWebComponent {
  /** What the dots show now — event identity and color, plus the overflow
   *  plus — so a rebind with the same events keeps its DOM (see `bind`). The
   *  dots live in the shadow root, which recycling leaves alone, so the
   *  signature and the DOM stay in step across the pool. */
  #boundDots: Option<string> = null;

  /** Dot each of `events` (up to `maxDots`) in `scope`'s category colours (`client.events`). */
  bind(
    events: EventItem[],
    scope: EventItemPresentation.Scope,
    showTooltip: () => void,
    hideTooltip: () => void,
    maxDots: Option<number>,
  ) {
    const limit = maxDots ?? events.length;
    const displayedEvents = events.slice(0, limit);
    const hasOverflow = events.length > limit;

    // The fixed-child reconcile rebinds every mounted indicator on every
    // rendered-columns change — every step of a scroll — and clearing and
    // rebuilding ~130 indicators' dots per step was the largest JS cost of a
    // held scroll burst on the bench calendar (project
    // sheet-scroll-smoothness, 2026-09-12). Same dots: keep the DOM and only
    // refresh the tooltip callbacks, which close over this call's events.
    const dots = displayedEvents
      .map((e) => e.id + ":" + EventItemPresentation.backgroundColor(e, scope))
      .join(",") + (hasOverflow ? "|+" : "");
    if (this.#boundDots !== dots) {
      this.#boundDots = dots;
      DomUtils.clearChildren(this.$("root"));
      for (const e of displayedEvents) {
        const $dot = Div.make();
        $dot.classList.add("dot");
        $dot.style.backgroundColor = EventItemPresentation.backgroundColor(e, scope);
        this.$("root").appendChild($dot);
      }
      if (hasOverflow) {
        const $plus = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "svg",
        );
        $plus.setAttribute("viewBox", "0 0 10 10");
        $plus.classList.add("plus");
        $plus.innerHTML = `<path d="M5 1v8M1 5h8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>`;
        this.$("root").appendChild($plus);
      }
    }

    this.onpointerenter = () => showTooltip();
    this.onpointerout = () => hideTooltip();
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: flex;
        flex-direction: column;
        align-items: center;
        cursor: pointer;
        border-radius: 3px;
        height: 8px;
      }

      :host([floating]) {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        z-index: 2000; /* ZIndex.SIDEBAR */
        width: calc(100% - 4px);
      }

      :host([top]) {
        bottom: calc(100% + 2px);
      }

      :host([bottom]) {
        top: calc(100% + 2px);
      }

      :host(:hover) {
        background: var(--bg-hover);
      }

      #root {
        height: 100%;
        border-radius: 4px;
        padding-inline: 3px;
        padding-block: 3px;
        justify-content: center;
        align-items: center;
        width: min-content;
      }

      .dot {
        height: var(--dot-height, 5px);
        width: 10px;
        margin-right: 3px;
        border-radius: 5px;
        background: var(--red);
      }

      .dot:last-child {
        margin-right: 0px;
      }

      .plus {
        width: calc(var(--dot-height, 5px) + 2px);
        height: calc(var(--dot-height, 5px) + 2px);
        color: var(--text-ter);
        flex-shrink: 0;
        margin-top: 0px;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-row id="root"></std-row>
  `;
}
